<template>
  <div class="c_image_gallery_consumer" :data-theme="theme" ref="imageGalleryConsumerSwipe">
    <div class="c_wrapper c_image_gallery_consumer__wrapper">
      <div class="c_image_gallery_consumer__media_wrapper" v-if="data.media.length">
        <div class="c_image_gallery_consumer__thumbnails" v-if="['lg'].includes($mq) && data.media.length > 1">
          <Button
            unstyle
            reset
            v-for="(thumb, index) in data.media"
            class="c_image_gallery_consumer__thumbnails__thumbnail"
            :key="`c_image_gallery_consumer__thumbnail--${index}`"
            @onClick="selectProduct(index)"
            :class="[{ 'c_image_gallery_consumer__thumbnails__thumbnail--active': index === activeIndex }]"
            :aria-controls="`c_image_gallery_consumer--${index}`"
          >
            <MediaImage :sources="[thumb.thumbnailSize]" :alt="thumb.altText || thumb.title" />
          </Button>
        </div>
        <div class="c_image_gallery_consumer__transition c_image_gallery_consumer__active_media_wrapper">
          <transition-group name="c_image_gallery_consumer-" tag="div">
            <div
              v-for="(img, index) in data.media"
              :key="`c_image_gallery_consumer__active--${index}`"
              v-show="index === activeIndex"
              :id="`c_image_gallery_consumer--${index}`"
            >
              <Button
                unstyle
                reset
                class="c_image_gallery_consumer__btn_lightbox"
                @onClick="$refs.imageGalleryConsumerOverlay.show()"
              >
                <MediaImage :sources="[img.fullSize]" :alt="img.title" />
              </Button>
            </div>
          </transition-group>
          <div class="c_image_gallery_consumer__navigation_indicator" v-if="data.media.length > 1">
            <Button
              unstyle
              reset
              class="c_image_gallery_consumer__prev"
              @onClick="previousSlide()"
              :aria-label="$t('Previous Slide')"
              :disabled="activeIndex === 0"
            >
              <IconSVG handle="arrow_icon_left" width="26" height="18" />
            </Button>
            <Button
              unstyle
              reset
              class="c_image_gallery_consumer__next"
              @onClick="nextSlide()"
              :disabled="activeIndex === data.media.length - 1"
              :aria-label="$t('Next Slide')"
            >
              <IconSVG handle="arrow_icon_right" width="26" height="18" />
            </Button>
          </div>
          <div
            v-if="['sm', 'md'].includes($mq) && data.media.length > 1"
            class="c_image_gallery_consumer__mobile_indicators"
          >
            <ul>
              <li
                v-for="(link, index) in data.media"
                :class="{ 'c_image_gallery_consumer__mobile_indicators--active': index === activeIndex }"
              >
                <Button reset unstyle @onClick="selectProduct(index)" :aria-label="link.altText || link.title">
                  <div aria-hidden="true" role="presentation"></div>
                </Button>
              </li>
            </ul>
          </div>
          <!-- TODO: refactor this -->
          <figcaption
            v-if="data.media?.[activeIndex]?.cardDescription || data.media?.[activeIndex]?.caption"
            v-html="data.media?.[activeIndex]?.cardDescription || data.media?.[activeIndex]?.caption"
          ></figcaption>
        </div>
      </div>
      <div class="c_image_gallery_consumer__content_wrapper">
        <h1 v-if="data.sectionTitle" v-html="data.sectionTitle"></h1>
        <div
          class="c_image_gallery_consumer__content c_2022--body"
          v-if="data.description"
          v-html="data.description"
        ></div>
        <div class="c_image_gallery_consumer__links">
          <ul>
            <li v-for="(linkData, index) in data.links" :key="index">
              <!-- Leaving Site -->
              <Button
                v-if="linkData.link?.url && linkData.enableModal"
                class="c_button_2022 c_button_2022__secondary"
                :aria-label="linkData.link?.ariaLabel"
                @onClick="triggerExternalLinkModal(linkData)"
                unstyle
                reset
              >
                <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              </Button>

              <!-- Video Link (YouTube / MP4) -->
              <Button
                v-else-if="hasVideo(linkData.link?.element) && linkData.link?.url && !linkData.enableModal"
                class="c_button_2022 c_button_2022__secondary"
                :aria-label="linkData.link?.ariaLabel"
                @onClick="handleVideoClick(linkData)"
                unstyle
                reset
              >
                <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              </Button>

              <!-- Regular Link -->
              <a
                v-else-if="linkData.link?.url && !linkData.enableModal"
                :href="linkData.link?.url"
                :target="linkData.link?.target"
                :aria-label="linkData.link?.ariaLabel"
                class="c_button_2022 c_button_2022__secondary"
              >
                <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <Overlay
      class="c_image_gallery_consumer__overlay"
      ref="imageGalleryConsumerOverlay"
      theme="image"
      :show-close-button="true"
    >
      <transition-group name="c_image_gallery_consumer-" tag="div">
        <div
          v-for="(img, index) in data.media"
          :key="`c_image_gallery_consumer__active--${index}`"
          v-show="index === activeIndex"
        >
          <MediaImage :sources="[img.fullSize]" :alt="img.title" />
        </div>
      </transition-group>

      <nav v-if="data.media.length > 1" class="c_image_gallery_consumer__lightbox_nav">
        <button
          class="c_image_gallery_consumer__prev"
          @click="previousSlide()"
          :aria-label="$t('Previous Slide')"
          :disabled="activeIndex === 0"
        >
          <IconSVG handle="arrow_left" width="30" height="30" />
        </button>
        <button
          class="c_image_gallery_consumer__next"
          @click="nextSlide()"
          :disabled="activeIndex === data.media.length - 1"
          :aria-label="$t('Next Slide')"
        >
          <IconSVG handle="arrow_right" width="30" height="30" />
        </button>
      </nav>
    </Overlay>
    <Overlay
      class="c_external_link__overlay"
      ref="external_link_overlay"
      :show-close-button="true"
      rounded
      @overlayHide="clearExternalLinkModal"
    >
      <ExternalLinkModal
        :disclaimers="externalLinkDisclaimer"
        :url="externalLinkUrl"
        @hideExternalLinkModal="hideExternalLinkModal"
      />
    </Overlay>
    <Overlay
      class="c_image_gallery_consumer__overlay"
      ref="videoBannerOverlay"
      theme="full"
      :show-close-button="true"
      @close="closeVideoModal"
    >
      <div class="c_box--pos--relative c_image_gallery_consumer__overlay__video">
        <!-- YouTube -->
        <MediaVideo
          v-if="videoSource === 'youtube'"
          loading="eager"
          :source="videoSource"
          :video-id="videoId"
          controls
          autoplay
          playsinline
        />

        <!-- MP4 -->
        <video
          v-else-if="videoSource === 'mp4' && videoUrl"
          class="c_image_gallery_consumer__overlay__video__player"
          :src="videoUrl"
          controls
          playsinline
          autoplay
        />
      </div>
    </Overlay>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, getCurrentInstance } from '@nuxtjs/composition-api';
import MediaImage from 'Components/image/MediaImage.vue';
import MediaVideo from 'Components/video/MediaVideo.vue';
import Overlay from 'Components/overlay/Overlay.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import Button from 'Components/button/Button.vue';
import { useSwipe } from 'Components/swipe/useSwipe.js';
import { useArrowNavigation } from 'Components/swipe/useArrowNavigation';
import ExternalLinkModal from 'Components/external_link_modal/ExternalLinkModal.vue';
import { getVideoPayload, hasVideo } from 'JS/global.js';

export default defineComponent({
  name: 'ProductHeaderGallery',
  props: {
    data: { type: Object, default: {} },
  },
  components: {
    Button,
    ExternalLinkModal,
    IconSVG,
    MediaImage,
    MediaVideo,
    Overlay,
  },
  setup(props) {
    const activeIndex = ref(0);
    const media = props.data?.media ?? [];
    const activeImage = ref(media[0] ?? null);
    const vm = getCurrentInstance();
    const proxy = vm.proxy;
    const isMobile = ref(false);
    const externalLinkDisclaimer = ref([]);
    const externalLinkUrl = ref('');
    const external_link_overlay = ref(null);

    // Ref for swipe container
    const imageGalleryConsumerSwipe = ref(null);
    useSwipe(imageGalleryConsumerSwipe, {
      onSwipe(dir) {
        if (!isMobile.value) return;
        if (dir === 'left') nextSlide();
        else if (dir === 'right') previousSlide();
      },
    });

    // Set active image to first in the array from GraphQL data
    activeImage.value = props.data.media[activeIndex.value];

    const theme = props.data.theme;

    const selectProduct = (clickedProductIndex) => {
      activeImage.value = props.data.media[clickedProductIndex];
      activeIndex.value = clickedProductIndex;
    };

    const previousSlide = () => {
      if (activeIndex.value !== 0) {
        activeIndex.value--;
      }
    };

    const nextSlide = () => {
      if (activeIndex.value !== props.data.media.length - 1) {
        activeIndex.value++;
      }
    };

    const triggerExternalLinkModal = (link) => {
      if (link?.disclaimerFootnote?.[0]) {
        externalLinkDisclaimer.value.push(link.disclaimerFootnote[0]);
      }
      externalLinkUrl.value = link.link?.url || 'https://kodak.com/en/';
      if (external_link_overlay.value && external_link_overlay.value.show) {
        external_link_overlay.value.show();
      }
    };
    const clearExternalLinkModal = () => {
      externalLinkDisclaimer.value = [];
      externalLinkUrl.value = '';
    };
    const hideExternalLinkModal = () => {
      if (external_link_overlay?.value && external_link_overlay?.value?.hide) {
        external_link_overlay.value.hide();
      }
    };

    // Allow left and right arrows to control
    // `activeIndex` value for accessibility
    useArrowNavigation(nextSlide, previousSlide);

    const videoBannerOverlay = ref(null);
    const videoSource = ref('');
    const videoId = ref('');
    const videoUrl = ref('');

    const openVideoModal = (el) => {
      const video = getVideoPayload(el);
      if (!video) return;

      if (video.type === 'youtube') {
        videoSource.value = video.source || 'youtube';
        videoId.value = video.id;
        videoUrl.value = null;
      } else if (video.type === 'mp4') {
        videoSource.value = 'mp4';
        videoId.value = null;
        videoUrl.value = video.url;
      }
      videoBannerOverlay.value?.show?.();
    };
    const closeVideoModal = () => {
      videoBannerOverlay.value?.hide?.();
      videoSource.value = null;
      videoId.value = null;
      videoUrl.value = null;
    };
    const handleVideoClick = (linkData) => {
      const el = linkData?.link?.element;
      openVideoModal(el);
    };

    onMounted(() => {
      isMobile.value = proxy.$mq === 'sm';
    });

    return {
      activeImage,
      activeIndex,
      clearExternalLinkModal,
      closeVideoModal,
      external_link_overlay,
      externalLinkDisclaimer,
      externalLinkUrl,
      getVideoPayload,
      handleVideoClick,
      hasVideo,
      hideExternalLinkModal,
      imageGalleryConsumerSwipe,
      MediaVideo,
      nextSlide,
      openVideoModal,
      previousSlide,
      selectProduct,
      theme,
      triggerExternalLinkModal,
      videoBannerOverlay,
      videoId,
      videoSource,
      videoUrl,
    };
  },
});
</script>

<style lang="scss">
.c_image_gallery_consumer {
  &[data-theme='dark'] {
    --image-gallery-consumer-bg: var(--color-black);
    --image-gallery-consumer-color: var(--color-white);
    --image-gallery-nav-btn-border: rgba(85, 85, 85, 1);
    --image-gallery-nav-btn-hover: rgba(47, 47, 47, 1);
    --image-gallery-nav-btn-bg: rgba(57, 57, 57, 1);
    --image-gallery-consumer-hover-color: var(--color-white);
    --mobile-indicator: rgba(85, 85, 85, 1);
    --mobile-indicator-active: var(--color-yellow);
  }
  &[data-theme='light'] {
    --image-gallery-consumer-bg: var(--color-white);
    --image-gallery-consumer-color: #1c1c1c;
    --image-gallery-nav-btn-border: rgba(221, 221, 221, 1);
    --image-gallery-nav-btn-hover: rgba(226, 226, 226, 1);
    --image-gallery-nav-btn-bg: rgba(246, 246, 246, 1);
    --image-gallery-consumer-hover-color: var(--color-white);
    --mobile-indicator: rgba(221, 221, 221, 1);
    --mobile-indicator-active: var(--color-black);
  }
}
.c_image_gallery_consumer {
  padding: px_to_rem(40) 0;
  background-color: var(--image-gallery-consumer-bg);
  color: var(--image-gallery-consumer-color);
  position: relative;
  touch-action: pan-y;
  @include mq(1024px) {
    padding: px_to_rem(80) 0;
    touch-action: auto;
  }
  .c_2022--body {
    @include fs_lh(18, 28, 18, 28);
  }
  h1 {
    @include font('graphik_bold');
    @include fs_lh(40, 48, 40, 48);
    margin-bottom: px_to_rem(32);
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: px_to_rem(20);
    @include mq(1024px) {
      flex-direction: row;
      gap: px_to_rem(40);
    }
    @include mq($mq_md_to_lg) {
      gap: px_to_rem(80);
    }
    & > div {
      flex: 1;
    }
  }
  &__active_media_wrapper {
    display: flex;
    flex-direction: column;
    gap: px_to_rem(12);
    position: relative;
    figure {
      overflow: hidden;
      aspect-ratio: 16/10;
      display: flex;
      justify-content: center;
      img {
        transition: transform ease-in-out 0.15s;
        transform: scale(1);
        object-fit: contain;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
    // lightbox button
    .c_image_gallery_consumer__btn_lightbox {
      padding: 0;
      border: none;
      background-color: transparent;
      position: relative;
      &:after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: rgba(255, 255, 255, 0);
        transition: background-color ease-in-out 0.15s;
      }
      &:hover {
        @media (hover: hover) and (pointer: fine) {
          cursor: zoom-in;
          img {
            transform: scale(1.05);
          }
          &:after {
            background-color: rgba(255, 255, 255, 0.05);
          }
        }
      }
    }
    figcaption {
      @include fs_lh(16, 24, 16, 24);
      flex-grow: 1;
      text-align: left;

      a {
        color: var(--image-gallery-consumer-color);
      }
    }
  }
  &__content_wrapper {
    max-width: 400px;
  }
  &__media_wrapper {
    display: flex;
    gap: px_to_rem(20);
    min-width: px_to_rem(340);
    @media (max-width: 380px) {
      min-width: 0;
    }
    @include mq($mq_md_to_lg) {
      gap: px_to_rem(40);
    }
  }
  .c_button_2022 {
    border-width: 1px;
  }
  &__links {
    margin-top: 2rem;
    ul {
      display: flex;
      gap: 1rem;
      flex-flow: row wrap;
    }
    a,
    button {
      color: var(--image-gallery-consumer-color);
      &:hover {
        color: var(--image-gallery-consumer-hover-color);
      }
    }
  }
  &__thumbnails {
    display: flex;
    flex-direction: column;
    gap: px_to_rem(12);
    &__thumbnail {
      position: relative;
      border-radius: px_to_rem(8);
      width: px_to_rem(50);
      height: px_to_rem(50);
      border: none;
      background-color: transparent;
      padding: 0;
      overflow: hidden;
      border: 1px solid transparent;
      &:hover {
        &:after {
          background-color: rgba(255, 183, 0, 0.4);
        }
      }
      &:after {
        content: '';
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 183, 0, 0);
        transition: background-color ease-in-out 0.15s;
      }
      &--active {
        border: 1px solid $color_yellow;
        &:after {
          background-color: rgba(255, 183, 0, 0.4);
        }
      }
      figure,
      picture {
        height: 100%;
      }
      img {
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
  }
  .c_overlay__content_wrapper {
    max-width: 80vw;
    @include mq(1024px) {
      max-width: 80vw;
    }
  }
  .c_image_gallery_consumer__lightbox_nav {
    button {
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      padding: 0;
      height: px_to_rem(40);
      width: px_to_rem(40);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.75);
      transition: color ease-in-out 0.15s;
      @include mq(1024px) {
        height: px_to_rem(60);
        width: px_to_rem(60);
      }
      &:disabled {
        cursor: not-allowed;
        color: rgba(255, 255, 255, 0.25);
        &:hover {
          cursor: not-allowed;
          color: rgba(255, 255, 255, 0.25);
        }
      }
      &:hover {
        color: rgba(255, 255, 255, 1);
      }
      svg {
        width: 100%;
        height: 100%;
      }
      &.c_image_gallery_consumer__prev {
        left: px_to_rem(-40);
        @include mq(1024px) {
          left: px_to_rem(-60);
        }
      }
      &.c_image_gallery_consumer__next {
        right: px_to_rem(-40);
        @include mq(1024px) {
          right: px_to_rem(-60);
        }
      }
    }
  }
  .c_image_gallery_consumer--enter-active,
  .c_image_gallery_consumer--leave-active {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .c_image_gallery_consumer--enter,
  .c_image_gallery_consumer--leave-to {
    opacity: 0;
  }
  .c_image_gallery_consumer--move {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .c_image_gallery_consumer--leave-active {
    display: none;
  }
  &__overlay {
    position: fixed;
    .slide {
      max-width: 100vw;
      height: 100%;
    }
  }
  &__navigation_indicator {
    gap: px_to_rem(8);
    display: none;
    @include mq($mq_md_to_lg) {
      display: flex;
    }
    button {
      @include transition(all 0.1s ease);
      height: px_to_rem(34);
      width: px_to_rem(34);
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9;
      border-radius: 50%;
      background-color: var(--image-gallery-nav-btn-bg);
      border: 1px solid var(--image-gallery-nav-btn-border);
      color: var(--image-gallery-consumer-color);
      .c_icon_svg {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      svg {
        width: 100%;
        height: 100%;
      }
      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      &.c_image_gallery_consumer__prev {
        margin-left: auto;
      }
      &:hover:not(:disabled) {
        background-color: var(--image-gallery-nav-btn-hover);
      }
    }
  }
  &__mobile_indicators {
    ul {
      display: flex;
      gap: px_to_rem(8);
    }
    li {
      flex-grow: 1;
    }
    button {
      width: 100%;
      height: px_to_rem(40);
      display: flex;
      align-items: center;
      & > div {
        height: px_to_rem(3);
        width: 100%;
        background-color: var(--mobile-indicator);
      }
    }
    &--active {
      button > div {
        background-color: var(--mobile-indicator-active);
      }
    }
  }
  .c_overlay__mask {
    backdrop-filter: blur(4px);
    background-color: rgba(0, 0, 0, 0.8);
  }
  .c_overlay__content {
    width: 75vw;
    .c_image img {
      max-height: 70vh;
      width: auto;
    }
  }
  &__overlay {
    background: rgba($color_black_raw, 0.5);

    @at-root #{&}__video {
      width: 90vw;
      max-width: 100%;
      overflow: hidden;
      @include mq($mq_sm_to_md) {
        width: 70vw;
      }
      @include mq($mq_md_to_lg) {
        width: px_to_rem(800);
      }
    }
  }
}
</style>
